import React, { FC, useMemo } from 'react';
import { useTranslation, Trans } from 'next-i18next';
import classNames from 'classnames';
import type { LandingResults } from 'src/common/types/api/v4/search/v1/landing';
import type { SeoData } from 'src/common/types/api/v4/rutter/v1/search/seo';
import type { LocationSeoData } from 'src/common/types/api/v4/rutter/v1/search/seo/location';
import type { Breadcrumb } from 'src/common/types/api/v4/boats/search';
import extractFaqsFromData from 'src/search/utils/extractFaqs';
import getPageDataFromBreadCrumbs from 'src/search/utils/getPageDataFromBreadcrumbs';
import getClassNames from 'src/common/utils/getClassNames';
import useGeolocation from 'src/search/hooks/useGeolocation';
import DlpHeader from 'src/search/components/DlpHeader';
import HowItWorks from 'src/common/components/HowItWorks';
import DlpJournal from 'src/common/components/LandingPageJournal';
import DlpFAQ from 'src/search/components/DlpFAQ';
import DlpTopDestinations from 'src/search/components/DlpTopDestinations';
import LandingPageReviews from 'src/common/components/LandingPageReviews';
import DlpBoatTypes from 'src/search/components/DlpBoatTypes';
import { DEFAULT_SLUG } from 'src/search/constants';
import CommonMetaTags from 'src/common/components/MetaTags/CommonMetaTags';
import DlpMetaFields from 'src/search/components/DlpMetaFields';
import LocationPrompt from 'src/search/components/LocationPrompt';
import DlpFilters from 'src/search/components/DlpFilters';
import DlpBreadcrumbsSection from 'src/search/components/DlpBreadcrumbsSection';
import DlpListingsInstabook from 'src/search/components/DlpListingsInstabook';
import DlpListingsRecommended from 'src/search/components/DlpListingsRecommended';
import DlpListingsPopular from 'src/search/components/DlpListingsPopular';
import styles from './DlpRoot.module.scss';

export type DlpRootProps = {
  content: Partial<LandingResults>;
  isNearbyLocationSearch: boolean;
  seo?: SeoData;
};

const isLocationSeoData = (data: SeoData): data is LocationSeoData => 'nearby_destinations' in data;

const DlpRoot: FC<DlpRootProps> = ({
  content,
  isNearbyLocationSearch,
  seo,
}) => {
  const [t] = useTranslation('discovery-landing-pages');
  const {
    // geocoordinates not currently used to do a client-side refresh
    // in order to do an updated search [sc-59816]
    showPrompt: showGeolocationPrompt,
    dismissModal,
    handleLocationPromptAccept,
    handleLocationPromptDecline,
  } = useGeolocation(isNearbyLocationSearch);

  const {
    primarySlug, region, country, locationSlug,
  } = getPageDataFromBreadCrumbs(
    content.breadcrumbs,
    content.slugs?.category?.slug,
    content.top_country_destinations,
    isNearbyLocationSearch,
  );

  /** Number of breadcrumbs */
  const count = isNearbyLocationSearch ? 0 : content.breadcrumbs?.length ?? 0;
  const plural = t(`${primarySlug}--plural`);
  const singular = t(`${primarySlug}--singular`);
  const h2 = t(`${primarySlug}--h2`);

  const isNearbyFallback = content.nearby_fallback;

  /** Header section title */
  const headerTitle = isNearbyFallback && count > 0 ? 'title--h1_near' : 'title--h1';

  /** Instabook section title */
  const instabookTitle = isNearbyFallback && count > 0
    ? t('title--instabook_near', { region: region?.name })
    : t('title--instabook', { region: region?.name, count });

  const instabookSubTitle = isNearbyFallback && count > 0
    ? t('title--instabook-h2_near', { plural, region: region?.name })
    : t('title--instabook-h2', { plural, region: region?.name, count });

  /** Our Favorites section title */
  const favoritesTitle = isNearbyFallback && count > 0
    ? t('title--favorite_near', { plural, region: region?.name })
    : t('title--favorite', { plural, region: region?.name, count });

  /**  Most Popular section titles  */
  const popularTitles = content.top_categories?.map((topCategory) => {
    const popularSlug = topCategory?.slug.split('/')[0];
    return t('title--popular', { plural: t(`${popularSlug}--plural`), region: region?.name, count });
  });

  const breadcrumbsAll = ((): Breadcrumb[] => {
    const defaultCrumb = {
      name: t(`${DEFAULT_SLUG}--plural`), // 'Boat Rentals'
      slug: DEFAULT_SLUG, // 'boat-rental'
      indexed: true, // SchemaBreadcrumbsList filters for indexed
    };
    if (content.breadcrumbs) {
      if (isNearbyLocationSearch) {
        // crumb 0 name is always 'All Categories' but the crumb and FAQSchema needs 'Boat Rentals'
        return [
          defaultCrumb, // crumb 0 should be 'Boat Rentals'
          ...content.breadcrumbs.slice(1), // crumb 1 will be the page category else 'Powerboats'
        ];
      }
      // if not nearby search, the breadcrumbs will include location items only
      // thus insert default crumb at index 0
      return [
        defaultCrumb,
        ...content.breadcrumbs,
      ];
    }
    return [defaultCrumb]; // should never happens
  })();

  const url = `https://www.getmyboat.com${content.canonical_url}`;
  const faqs = useMemo(
    () => (seo?.copy?.faqs ? extractFaqsFromData([seo.copy.faqs], url) : undefined),
    [seo?.copy?.faqs, url],
  );
  const nearbyDestinations = seo && isLocationSeoData(seo) ? seo.nearby_destinations : null;
  const hasInstabookListings = content.instabook_listings
    && content.instabook_listings.length > 0;

  const boatTypesCount = content.listing_types?.length ?? 0;

  const pageStats = {
    average: content.reviews?.average,
    total: content.reviews?.total,
    breadcrumbs: breadcrumbsAll,
  };

  return (
    <>
      <CommonMetaTags
        noindex={false}
        canonicalUrl={content.canonical_url}
      />
      <DlpMetaFields
        content={content}
        breadcrumbs={breadcrumbsAll}
        description={seo?.copy?.description}
        title={seo?.copy?.title ?? t('title--metadata-title')} // Should come from Bridge - match Search
        faqs={faqs}
      />
      <LocationPrompt
        modalOpen={showGeolocationPrompt}
        closeModal={dismissModal}
        onAccept={handleLocationPromptAccept}
        onDecline={handleLocationPromptDecline}
      />

      <DlpHeader
        title={(
          <Trans
            t={t}
            i18nKey={headerTitle}
            components={{
              br: <br className={styles.mobileOnly} />,
            }}
            values={{
              plural,
              region: region?.name,
              count,
            }}
          />
        )}
        subTitle={(
          <Trans
            t={t}
            i18nKey="title--h2"
            components={{ br: <br className={styles.mobileOnly} /> }}
            values={{ h2 }}
          />
        )}
      />

      <div className={styles.wrapper}>
        {hasInstabookListings && (
          <div className={styles.stackedSection}>
            <DlpListingsInstabook
              title={instabookTitle}
              subTitle={instabookSubTitle}
              listings={content.instabook_listings}
              categorySlug={primarySlug}
              locationSlug={isNearbyLocationSearch ? undefined : locationSlug}
              data-gulper-section="BookNow"
            />
          </div>
        )}
        {content.recommended_listings
          && content.recommended_listings.length > 0 && (
            <div className={styles.stackedSection}>
              <DlpListingsRecommended
                title={favoritesTitle}
                listings={content.recommended_listings}
                categorySlug={primarySlug}
                locationSlug={isNearbyLocationSearch ? undefined : locationSlug}
                pageStats={pageStats}
                data-gulper-section="Search"
              />
            </div>
        )}
        <div className={styles.suggestedSearches}>
          <DlpFilters
            price={content.filter?.price}
            primarySlug={primarySlug}
            locationSlug={isNearbyLocationSearch ? undefined : locationSlug}
          />
        </div>
        <div className={getClassNames(styles, 'boatTypesWrapper', classNames({
          hasAtLeastOne: boatTypesCount >= 1,
          hasAtLeastTwo: boatTypesCount >= 2,
          hasAtLeastThree: boatTypesCount >= 3,
        }))}
        >
          <DlpBoatTypes
            key={url}
            title={t('title--boatTypes', { region: region?.name, count })}
            listingTypes={content.listing_types}
            locationSlug={isNearbyLocationSearch ? undefined : locationSlug}
          />
        </div>
        {nearbyDestinations
      && nearbyDestinations.length > 0 && (
        <div className={styles.stackedSection}>
          <DlpTopDestinations
            title={t('title--nearby-locations', { singular, region: region?.name, count })}
            destinations={nearbyDestinations}
            primarySlug={primarySlug}
          />
        </div>
        )}
        {content.top_categories
      && content.top_categories.length > 0 && (
        <div className={styles.stackedSection}>
          <DlpListingsPopular
            topCategories={content.top_categories}
            popularTitles={popularTitles}
            locationSlug={isNearbyLocationSearch ? undefined : locationSlug}
          />
        </div>
        )}
        {content.reviews?.top_reviews
      && content.reviews.top_reviews.length > 0 && (
        <LandingPageReviews
          headingSize="heading-3"
          title={t('title--reviews', { region: region?.name, count })}
          reviews={content.reviews.top_reviews}
        />
        )}
        {content.top_country_destinations
      && content.top_country_destinations?.length > 0 && (
        <div className={styles.stackedSection}>
          <DlpTopDestinations
            // Will always return a country, so we don't need 'count'
            title={t('title--top-locations', { singular, country: country?.name })}
            destinations={content.top_country_destinations}
            primarySlug={primarySlug}
          />
        </div>
        )}
        <HowItWorks headingSize="heading-3" />
        {content.journal_pages?.meta?.total_count
      && content.journal_pages.meta.total_count > 0 && (
        <DlpJournal
          cardCta={t('journal--card-cta')}
          headingSize="heading-3"
          journals={content.journal_pages.items}
          sectionCta={t('journal--cta')}
          title={t('journal--title')}
        />
        )}
        {faqs && faqs?.length > 0 && (
          <div className={styles.stackedSection}>
            <DlpFAQ
              faqs={faqs}
              title={t('faq--section-title')}
            />
          </div>
        )}
        {breadcrumbsAll && (
          <DlpBreadcrumbsSection breadcrumbs={breadcrumbsAll} />
        )}
      </div>
    </>
  );
};

export default DlpRoot;
